import { motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Flame, Trophy, Target } from 'lucide-react';

interface DailyChallenge {
  id: string;
  title: string;
  description: string;
  target: number;
  progress: number;
  reward: number;
  completed: boolean;
}

interface DailyChallengeCardProps {
  challenges: DailyChallenge[];
  streak: number;
}

export default function DailyChallengeCard({ challenges, streak }: DailyChallengeCardProps) {
  const completedCount = challenges.filter(c => c.completed).length;
  
  return (
    <Card className="glass-card p-4 space-y-4">
      {/* Streak Display */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Flame className="h-5 w-5 text-orange-500" />
          <span className="font-bold text-sm">Daily Streak</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-2xl font-black text-orange-500">{streak}</span>
          <span className="text-sm text-muted-foreground">days</span>
        </div>
      </div>

      {/* Challenges Header */}
      <div className="flex items-center justify-between border-t border-border/30 pt-3">
        <div className="flex items-center gap-2">
          <Target className="h-4 w-4 text-primary" />
          <span className="font-bold text-sm">Daily Challenges</span>
        </div>
        <span className="text-xs text-muted-foreground">
          {completedCount}/{challenges.length} completed
        </span>
      </div>

      {/* Challenge List */}
      <div className="space-y-3">
        {challenges.map((challenge, index) => (
          <motion.div
            key={challenge.id}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`p-3 rounded-lg border ${
              challenge.completed 
                ? 'bg-primary/10 border-primary/30' 
                : 'bg-secondary/30 border-border/30'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {challenge.completed ? (
                  <Trophy className="h-4 w-4 text-primary" />
                ) : (
                  <div className="h-4 w-4 rounded-full border-2 border-muted-foreground/50" />
                )}
                <span className={`font-bold text-sm ${challenge.completed ? 'text-primary' : ''}`}>
                  {challenge.title}
                </span>
              </div>
              <span className="text-xs font-bold text-primary">+{challenge.reward} XP</span>
            </div>
            
            <p className="text-xs text-muted-foreground mb-2">{challenge.description}</p>
            
            <div className="flex items-center gap-2">
              <Progress 
                value={(challenge.progress / challenge.target) * 100} 
                className="h-1.5 flex-1"
              />
              <span className="text-xs font-bold text-muted-foreground">
                {challenge.progress}/{challenge.target}
              </span>
            </div>
          </motion.div>
        ))}
      </div>
    </Card>
  );
}